// src/api.js
import axios from 'axios';
import authApi from './authApi';
import { set as idbSet, clear as idbClear } from 'idb-keyval';

/**
 * Global helper to purge sensitive auth tokens & caches from localStorage,
 * sessionStorage, and IndexedDB, then force redirect to login screen.
 */
export const clearAuthAndRedirect = async () => {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.clear();
  } catch (e) {
    console.error('Failed clearing localStorage:', e);
  }

  try {
    sessionStorage.clear();
  } catch (e) {
    console.error('Failed clearing sessionStorage:', e);
  }

  try {
    await idbClear();
  } catch (e) {
    console.error('Failed clearing IndexedDB cache:', e);
  }

  if (typeof window !== 'undefined' && window.location) {
    window.location.href = '/login';
  }
};

// Flag to prevent multiple simultaneous refreshes
let isRefreshing = false;
// Queue to hold requests that are waiting for the token refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

api.interceptors.request.use(
  (config) => {
    const isPublic = config.skipAuth || config.skipAuthRedirect || config.url?.includes('/api/v2/bookings/public/');
    const token = localStorage.getItem('access_token');
    if (token && !isPublic) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Notify app that we are online
    window.dispatchEvent(new Event('onlineModeTriggered'));

    // Cache specific GET requests for offline use with standardized keys
    if (response.config.method === 'get') {
      const url = response.config.url;
      const params = response.config.params || {};
      if (url && (url.includes('/api/solosync2/routines/') || url.includes('/api/solosync2/v2/routines/'))) {
        if (params.archived === 'true') {
          idbSet('cached_past_routines_list', response.data).catch(err => console.error('Cache write failed', err));
        } else {
          idbSet('cached_routines_list', response.data).catch(err => console.error('Cache write failed', err));
        }
      } else if (url && url.includes('/api/solosync2/user/settings/')) {
        idbSet('cached_user_settings', response.data).catch(err => console.error('Cache write failed', err));
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // A true network failure means the device has no connectivity — no response
    // object is present AND the browser reports we're offline (or error message
    // confirms a network-level failure). HTTP 4xx/5xx responses from the server
    // mean we ARE online and must NOT trigger the offline banner.
    const isTrueNetworkError =
      !error.response &&
      (!navigator.onLine ||
        error.message === 'Network Error' ||
        error.isNetworkError === true);

    // A 503 on a social route means the kill switch is off — dispatch a targeted event
    // and do NOT trigger the generic offline banner for this case.
    const isSocialRoute503 =
      error.response?.status === 503 &&
      originalRequest?.url?.includes('/api/solosync2/social/');

    if (isSocialRoute503) {
      window.dispatchEvent(new CustomEvent('socialFeaturesDisabled'));
    } else if (isTrueNetworkError && originalRequest?.method === 'get') {
      // Only trigger offline mode on genuine connectivity loss
      window.dispatchEvent(new Event('offlineModeTriggered'));
    }

    const isPublicEndpoint = originalRequest?.url?.includes('/api/v2/bookings/public/') || originalRequest?.skipAuthRedirect;

    if (!isPublicEndpoint && error.response && error.response.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      // Early exit if we don't have a refresh token at all
      if (!refreshToken) {
        await clearAuthAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await authApi.post('/api/token/refresh/', {
          refresh: refreshToken,
        });

        const newAccessToken = response.data.access;
        localStorage.setItem('access_token', newAccessToken);
        if (response.data.refresh) {
          localStorage.setItem('refresh_token', response.data.refresh);
        }
        api.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;
        
        processQueue(null, newAccessToken);
        
        originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Only log out if the server explicitly rejects the refresh (e.g., token invalid or expired)
        if (refreshError.response && (refreshError.response.status === 401 || refreshError.response.status === 400)) {
          await clearAuthAndRedirect();
        } else if (!refreshError.response) {
          // Flag as a network issue so callers can handle gracefully
          refreshError.isNetworkError = true;
        }
        
        // Fail the request so the app can recover later
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const logChallengeSession = async (payload) => {
  return await api.post('/api/solosync2/challenges/log/', payload);
};

export const markCommentRead = async (logId) => {
  return await api.post(`/api/solosync2/logs/${logId}/mark_comment_read/`);
};

export const fetchUnreadLogs = async () => {
  return await api.get('/api/solosync2/logs/unread/');
};

export const getUnreadCommentsCount = async () => {
  return await api.get('/api/solosync2/logs/unread_comments_count/');
};

export const getChallengeLeaderboard = async (routineId) => {
  return await api.get(`/api/solosync2/v2/challenges/leaderboard/?routine_id=${routineId}`);
};

export const getUserBadges = async () => {
  return await api.get('/api/solosync2/challenges/badges/');
};

export const verifyChallengeScore = async (recordId, pin) => {
  return await api.post(`/api/solosync2/v2/challenges/verify-result/`, { result_id: recordId, auth_pin: pin });
};

export const linkAccount = async (code) => {
  return await api.post('/api/solosync2/v2/link-account/', { access_code: code });
};

// ---------------------------------------------------------------------------
// V2 Booking Endpoints
// ---------------------------------------------------------------------------

/**
 * Fetches court availability matrix for a given venue and date.
 */
export const fetchAvailability = async (venueSlug, date) => {
  const params = typeof venueSlug === 'object'
    ? { ...venueSlug, venue_slug: venueSlug.venue || venueSlug.venue_slug }
    : { venue: venueSlug, venue_slug: venueSlug, date };
  const response = await api.get('/api/v2/bookings/availability/', { params });
  return response.data;
};

/**
 * Creates a court booking.
 */
export const createBooking = async (payload) => {
  const response = await api.post('/api/v2/bookings/book/', payload);
  return response.data;
};

/**
 * Fetches user's next upcoming booking for dashboard widget.
 */
export const fetchMyNextBooking = async () => {
  const response = await api.get('/api/v2/bookings/my-next/');
  return response.data;
};

/**
 * Fetches public availability matrix (no login redirect on 401/403).
 */
export const fetchPublicAvailability = async (venueSlug, date) => {
  const params = typeof venueSlug === 'object' ? venueSlug : { date };
  const slug = typeof venueSlug === 'object' ? (venueSlug.venueSlug || venueSlug.venue) : venueSlug;
  const url = slug ? `/api/v2/bookings/public/${slug}/` : '/api/v2/bookings/public/';
  const response = await api.get(url, {
    params,
    skipAuth: true,
    skipAuthRedirect: true,
  });
  return response.data;
};

/**
 * Creates a public booking / guest reservation (no login redirect on 401/403).
 */
export const createPublicBooking = async (venueSlugOrPayload, payload) => {
  let url = '/api/v2/bookings/public/';
  let body = payload;
  if (typeof venueSlugOrPayload === 'string') {
    url = `/api/v2/bookings/public/${venueSlugOrPayload}/`;
  } else {
    body = venueSlugOrPayload;
    if (body?.venue_slug || body?.venue) {
      url = `/api/v2/bookings/public/${body.venue_slug || body.venue}/`;
    }
  }

  // Ensure public_name and public_contact are mapped
  if (body) {
    if (!body.public_name && body.guest_name) {
      body.public_name = body.guest_name;
    }
    if (!body.public_contact) {
      if (body.guest_phone && body.guest_email) {
        body.public_contact = `${body.guest_phone} (${body.guest_email})`;
      } else {
        body.public_contact = body.guest_phone || body.guest_email || '';
      }
    }
  }

  const response = await api.post(url, body, {
    skipAuth: true,
    skipAuthRedirect: true,
  });
  return response.data;
};

// ---------------------------------------------------------------------------
// Social Feature API — governed by the backend kill switch.
// All social functions catch 503s explicitly: they dispatch 'socialFeaturesDisabled'
// and return null so callers receive a clean null rather than an unhandled rejection.
// ---------------------------------------------------------------------------

/**
 * Fetches global app config (no auth required).
 * Returns { social_features_enabled: bool } or null on failure.
 */
export const fetchAppConfig = async () => {
  try {
    const cached = localStorage.getItem('solosync_app_config');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          return parsed.data;
        }
      } catch (e) {
        localStorage.removeItem('solosync_app_config');
      }
    }

    // Use the base axios instance (no auth header) since the config endpoint is public
    const { default: axios } = await import('axios');
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/solosync2/config/`,
      { timeout: 4000 }
    );
    if (response?.data) {
      localStorage.setItem('solosync_app_config', JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
    }
    return response.data;
  } catch (err) {
    console.warn('fetchAppConfig failed:', err);
    const cached = localStorage.getItem('solosync_app_config');
    if (cached) {
      try {
        return JSON.parse(cached).data;
      } catch (e) {}
    }
    return null;
  }
};

/**
 * Fetches the paginated global social leaderboard.
 * Returns the response data object or null on failure / kill switch off.
 */
export const fetchLeaderboard = async (page = 1) => {
  try {
    const response = await api.get('/api/solosync2/social/leaderboard/', {
      params: { page },
    });
    return response.data;
  } catch (err) {
    if (err.response?.status === 503) {
      window.dispatchEvent(new CustomEvent('socialFeaturesDisabled'));
    }
    return null;
  }
};

/**
 * Fetches a user's social profile.
 * Returns the profile data object, or null on failure / kill switch off.
 */
export const fetchSocialProfile = async (userId) => {
  try {
    const response = await api.get(`/api/solosync2/social/profile/${userId}/`);
    return response.data;
  } catch (err) {
    if (err.response?.status === 503) {
      window.dispatchEvent(new CustomEvent('socialFeaturesDisabled'));
    }
    return null;
  }
};

/**
 * Manages social connections (follow, unfollow, accept, etc.).
 * - GET: list current connections & pending requests.
 * - POST: payload e.g. { action: 'follow', target_user_id: 42 }
 * Returns the response data or null on failure / kill switch off.
 */
/**
 * Fetches the categorised Discovery lists.
 * Returns { academy_users: [], client_users: [] } or null on failure / kill switch off.
 */
export const fetchDiscoverList = async () => {
  try {
    const response = await api.get('/api/solosync2/social/discover/');
    return response.data;
  } catch (err) {
    if (err.response?.status === 503) {
      window.dispatchEvent(new CustomEvent('socialFeaturesDisabled'));
    }
    return null;
  }
};

export const manageConnection = async (payload = null) => {
  try {
    let response;
    if (payload === null) {
      // GET — retrieve connections list
      response = await api.get('/api/solosync2/social/connections/');
    } else {
      // POST — create / update a connection
      response = await api.post('/api/solosync2/social/connections/', payload);
    }
    return response.data;
  } catch (err) {
    if (err.response?.status === 503) {
      window.dispatchEvent(new CustomEvent('socialFeaturesDisabled'));
    }
    return null;
  }
};

// ---------------------------------------------------------------------------
// Password Management & Recovery API
// ---------------------------------------------------------------------------

/**
 * Changes authenticated user password.
 * @param {Object} payload { old_password, new_password }
 */
export const changePassword = async (payload) => {
  const body = {
    old_password: payload.old_password || payload.current_password || payload.currentPassword || '',
    new_password: payload.new_password || payload.newPassword || '',
  };
  const response = await api.post('/api/solosync2/user/change-password/', body);
  return response.data;
};

/**
 * Updates authenticated user profile / settings (e.g. email, names, preferences).
 * @param {Object} payload
 */
export const updateUserSettings = async (payload) => {
  const response = await api.patch('/api/solosync2/user/settings/', payload);
  return response.data;
};

/**
 * Requests a password reset link for the given email address.
 * @param {string} email
 */
export const requestPasswordReset = async (email) => {
  const response = await authApi.post('/api/auth/password/reset/', { email });
  return response.data;
};

/**
 * Confirms password reset with uid, token, and new password.
 * @param {Object} payload { uid, token, new_password }
 */
export const confirmPasswordReset = async (payload) => {
  const response = await authApi.post('/api/auth/password/reset/confirm/', payload);
  return response.data;
};

export default api;